import { PlanGenerationAttemptOut } from "@providers/hop-ord-server/api";
import {
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  Stack,
  Button,
  TableContainer,
} from "@mui/material";
import { toLocaleDateTimeString } from "@utils";
import { useState } from "react";
import { StatusPageDialog } from "./StatusPageComponents";

const formatDateTime = (dateTimeString: string | null) => {
  if (!dateTimeString) return { date: "-", time: "" };

  const dateTime = toLocaleDateTimeString(dateTimeString);
  const parts = dateTime.split(", ");

  return {
    date: parts[0] || "-",
    time: parts[1] || "",
  };
};

interface Props {
  plans: PlanGenerationAttemptOut[];
}

interface SelectedPlanData {
  warnings: PlanGenerationAttemptOut["warnings"];
  error: string | null;
  finishedAt: string | null;
  open: boolean;
}

const CompletedPlans = ({ plans }: Props) => {
  const [selectedData, setSelectedData] = useState<SelectedPlanData>({
    warnings: [],
    error: null,
    finishedAt: null,
    open: false,
  });

  const handleWarningClick = (
    warnings: PlanGenerationAttemptOut["warnings"],
    error: string | null = null,
    finishedAt: string | null = null,
  ) => {
    setSelectedData({ warnings, error, finishedAt, open: true });
  };

  const handleCloseDialog = () => {
    setSelectedData({
      warnings: [],
      error: null,
      finishedAt: null,
      open: false,
    });
  };

  return (
    <Card sx={{ padding: 2 }}>
      <Typography variant="subtitle1">Previous Plans</Typography>
      <TableContainer sx={{ maxHeight: "400px" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "254px", minWidth: "200px" }}>
                Automated Template
              </TableCell>
              <TableCell sx={{ width: "278px", minWidth: "220px" }}>
                Related Prescription
              </TableCell>
              <TableCell sx={{ width: "136px", minWidth: "120px" }}>
                Initiated
              </TableCell>
              <TableCell sx={{ width: "136px", minWidth: "120px" }}>
                Completed
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans?.map((plan, index) => {
              const hasError = !plan.success;
              const hasWarnings = plan?.warnings?.length > 0;
              const showButtons = hasError || hasWarnings;
              const initiatedDateTime = formatDateTime(plan.planningStartedAt);
              const completedDateTime = formatDateTime(
                plan.success ? plan.finishedAt : null,
              );

              return (
                <TableRow key={index}>
                  <TableCell>
                    <Stack
                      sx={{
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="body2">
                        {plan.templateName}
                      </Typography>
                      {showButtons && (
                        <Stack
                          direction="row"
                          sx={{
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {hasError && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() =>
                                handleWarningClick(
                                  plan.warnings,
                                  plan.error,
                                  plan.finishedAt,
                                )
                              }
                            >
                              error
                            </Button>
                          )}
                          {hasWarnings && !hasError && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => handleWarningClick(plan.warnings)}
                            >
                              {plan.warnings.length} warning
                              {plan.warnings.length === 1 ? "" : "s"}
                            </Button>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2">
                        {plan?.relatedPrescription || "-"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        {plan.machine}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2">
                        {initiatedDateTime.date}
                      </Typography>
                      {initiatedDateTime.time && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {initiatedDateTime.time}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2">
                        {completedDateTime.date}
                      </Typography>
                      {completedDateTime.time && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {completedDateTime.time}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <StatusPageDialog
        warnings={selectedData.warnings}
        error={selectedData.error}
        finishedAt={selectedData.finishedAt}
        open={selectedData.open}
        onClose={handleCloseDialog}
      />
    </Card>
  );
};

export default CompletedPlans;
